import Immutable, { List, OrderedSet } from 'immutable';
import { IntegrationsSelectedItem } from 'records/IntegrationsSelectedItem';
import { AppState, AppStateActionParams } from 'records/AppState';
import { Markup } from 'records/Markup';
import { NewRFI } from 'records/NewRFI';
import { NewRFIForwardActivity } from 'records/NewRFIForwardActivity';
import { ProjectDriveSelectedItem } from 'records/ProjectDriveSelectedItem';
import { RFI, RFIId, StudioData } from 'records/RFI';
import { RFIAnswer } from 'records/RFIAnswer';
import { RFIAttachment } from 'records/RFIAttachment';
import { RFIFile } from 'records/RFIFile';
import { StudioProjectFileData } from 'records/StudioProjectFile';
import { getMarkup } from 'services/markupService';
import {
  attachRfiForwardDiscussions,
  createRFI,
  createRFIAnswer,
  createRFIDraft,
  createRFIForwardActivity,
  deleteRFIDraft,
  deleteRfiForwardDiscussion,
  getRFIDrafts,
  getRFIs,
  modifyRFIForwardingList,
  postMarkupsToRFI,
  publishRFIDraft,
  reopenRFI,
  updateRFIDraft,
} from 'services/rfiService';

type SelectedRFIs = Immutable.Set<RFIId>;

export interface RFIActions {
  getRFIs: (actionParams: AppStateActionParams) => Promise<AppState>;

  setViewRFI: (actionParams: AppStateActionParams, viewRFI: NewRFI) => AppState;

  setViewEdittedRFI: (
    actionParams: AppStateActionParams,
    viewEdittedRFI: NewRFI
  ) => AppState;

  setSelectedRFIs: (
    actionParams: AppStateActionParams,
    selectedRFIs: Immutable.Set<RFIId>
  ) => AppState;

  addSelectedRFI: (
    actionParams: AppStateActionParams,
    RFIId: RFIId
  ) => AppState;

  deleteSelectedRFI: (
    actionParams: AppStateActionParams,
    RFIId: RFIId
  ) => AppState;

  createRFI: (
    actionParams: AppStateActionParams,
    newRFI: NewRFI,
    files: Immutable.List<RFIFile>,
    projectDriveItems: List<ProjectDriveSelectedItem>,
    integrationItems: List<IntegrationsSelectedItem>,
    studioItems: List<StudioProjectFileData>,
    studioData: StudioData
  ) => Promise<AppState>;

  createRFIAnswer: (
    actionParams: AppStateActionParams,
    rfi: RFI,
    answer: RFIAnswer,
    files: Immutable.List<RFIFile>,
    projectDriveItems: List<ProjectDriveSelectedItem>,
    integrationItems: List<IntegrationsSelectedItem>,
    studioItems: List<StudioProjectFileData>,
    studioData: StudioData
  ) => Promise<AppState>;

  createRFIDraft: (
    actionParams: AppStateActionParams,
    newRFIDraft: NewRFI,
    files: Immutable.List<RFIFile>,
    projectDriveItems: List<ProjectDriveSelectedItem>,
    integrationItems: List<IntegrationsSelectedItem>,
    studioItems: List<StudioProjectFileData>,
    studioData: StudioData
  ) => Promise<AppState>;

  deleteRFIDraft: (
    actionParams: AppStateActionParams,
    RFIId: RFIId,
    ProjectID: string,
    previousversionid?: string
  ) => Promise<AppState>;

  updateRFIDraft: (
    actionParams: AppStateActionParams,
    rfiid: RFIId,
    newRFI: NewRFI,
    files: Immutable.List<RFIFile>,
    attachmentsToDelete: Immutable.List<RFIAttachment>,
    projectDriveItems: List<ProjectDriveSelectedItem>,
    integrationItems: List<IntegrationsSelectedItem>,
    studioItems: List<StudioProjectFileData>,
    studioData: StudioData
  ) => Promise<AppState>;

  publishRFIDraft: (
    actionParams: AppStateActionParams,
    rfiid: RFIId,
    rfi: NewRFI,
    files: Immutable.List<RFIFile>,
    removedAttachments: Immutable.List<RFIAttachment>,
    projectDriveItems: List<ProjectDriveSelectedItem>,
    integrationItems: List<IntegrationsSelectedItem>,
    studioItems: List<StudioProjectFileData>,
    studioData: StudioData
  ) => Promise<AppState>;

  postMarkupsToRFI: (
    actionParams: AppStateActionParams,
    rfi: RFI,
    markups: Immutable.List<Markup>
  ) => AsyncGenerator<AppState>;

  createRFIForwardActivity: (
    actionParams: AppStateActionParams,
    rfi: RFI,
    projectid: string,
    files: Immutable.List<RFIFile>,
    projectDriveItems: List<ProjectDriveSelectedItem>,
    integrationItems: List<IntegrationsSelectedItem>,
    studioItems: List<StudioProjectFileData>,
    studioData: StudioData,
    forwardActivity: NewRFIForwardActivity
  ) => Promise<AppState>;

  modifyRFIForwardingList: (
    actionParams: AppStateActionParams,
    rfiid: RFIId,
    projectid: string,
    forwardList: OrderedSet<string>
  ) => Promise<AppState>;

  attachRfiForwardDiscussions: (
    actionParams: AppStateActionParams,
    rfi: RFI,
    projectid: string,
    discussionIds: Immutable.Set<string>
  ) => Promise<AppState>;

  deleteRfiForwardDiscussion: (
    actionParams: AppStateActionParams,
    rfiid: string,
    projectid: string,
    id: string
  ) => Promise<AppState>;

  reopenRFI: (
    actionParams: AppStateActionParams,
    newRFI: NewRFI,
    files: Immutable.List<RFIFile>,
    projectDriveItems: List<ProjectDriveSelectedItem>,
    integrationItems: List<IntegrationsSelectedItem>,
    studioItems: List<StudioProjectFileData>,
    studioData: StudioData
  ) => Promise<AppState>;
}

const rfiActions: RFIActions = {
  setViewRFI: ({ state }: AppStateActionParams, viewRFI: NewRFI): AppState => {
    return state().set('viewRFI', viewRFI);
  },
  setViewEdittedRFI: (
    { state }: AppStateActionParams,
    viewEdittedRFI: NewRFI
  ): AppState => {
    return state().set('viewEdittedRFI', viewEdittedRFI);
  },
  setSelectedRFIs: (
    { state }: AppStateActionParams,
    selectedRFIs: SelectedRFIs
  ): AppState => {
    return state().set('selectedRFIs', selectedRFIs);
  },

  getRFIs: async ({ state }: AppStateActionParams): Promise<AppState> => {
    const { selectedProject } = state();

    const [rfis, rfiDrafts] = await Promise.all([
      getRFIs(selectedProject),
      getRFIDrafts(selectedProject),
    ]);

    const rfiMap = rfis
      .concat(rfiDrafts)
      .reduce(
        (acc, rfi) => acc.set(rfi.id, rfi),
        Immutable.OrderedMap<RFIId, RFI>()
      );

    return state().set('rfis', rfiMap);
  },

  addSelectedRFI: ({ state }: AppStateActionParams, rfiId: RFIId): AppState => {
    return state().update(
      'selectedRFIs',
      (selectedRFIs: SelectedRFIs): SelectedRFIs => selectedRFIs.add(rfiId)
    );
  },

  deleteSelectedRFI: (
    { state }: AppStateActionParams,
    PunchId: string
  ): AppState => {
    return state().update(
      'selectedRFIs',
      (selectedRFIs: SelectedRFIs): SelectedRFIs => selectedRFIs.delete(PunchId)
    );
  },

  createRFI: async (
    { state }: AppStateActionParams,
    newRFI: NewRFI,
    files: Immutable.List<RFIFile>,
    projectDriveItems: List<ProjectDriveSelectedItem>,
    integrationItems: List<IntegrationsSelectedItem>,
    studioItems: List<StudioProjectFileData>,
    studioData: StudioData
  ): Promise<AppState> => {
    const createdRFI = await createRFI(
      newRFI,
      files,
      projectDriveItems,
      integrationItems,
      studioItems,
      studioData
    );

    return state().setIn(['rfis', createdRFI.id], createdRFI);
  },

  createRFIAnswer: async (
    { state }: AppStateActionParams,
    rfi: RFI,
    answer: RFIAnswer,
    files: Immutable.List<RFIFile>,
    projectDriveItems: List<ProjectDriveSelectedItem>,
    integrationItems: List<IntegrationsSelectedItem>,
    studioItems: List<StudioProjectFileData>,
    studioData: StudioData
  ): Promise<AppState> => {
    const createdRFIAnswer = await createRFIAnswer(
      rfi,
      answer,
      files,
      projectDriveItems,
      integrationItems,
      studioItems,
      studioData
    );

    return state().setIn(['rfis', rfi.id, 'answer'], createdRFIAnswer);
  },

  createRFIDraft: async (
    { state }: AppStateActionParams,
    newRFIDraft: NewRFI,
    files: Immutable.List<RFIFile>,
    projectDriveItems: List<ProjectDriveSelectedItem>,
    integrationItems: List<IntegrationsSelectedItem>,
    studioItems: List<StudioProjectFileData>,
    studioData: StudioData
  ): Promise<AppState> => {
    const createdRFIDraft = await createRFIDraft(
      newRFIDraft,
      files,
      projectDriveItems,
      integrationItems,
      studioItems,
      studioData
    );

    let newstate = state().setIn(['rfis', createdRFIDraft.id], createdRFIDraft);
    if (createdRFIDraft.previousversionid !== '') {
      newstate = newstate.setIn(
        ['rfis', createdRFIDraft.previousversionid, 'nextversionid'],
        createdRFIDraft.id
      );
    }

    return newstate;
  },

  updateRFIDraft: async (
    { state }: AppStateActionParams,
    rfiid: RFIId,
    newRFI: NewRFI,
    files: Immutable.List<RFIFile>,
    attachmentsToDelete: Immutable.List<RFIAttachment>,
    projectDriveItems: List<ProjectDriveSelectedItem>,
    integrationItems: List<IntegrationsSelectedItem>,
    studioItems: List<StudioProjectFileData>,
    studioData: StudioData
  ): Promise<AppState> => {
    const updatedRFIDraft = await updateRFIDraft(
      rfiid,
      newRFI,
      files,
      attachmentsToDelete,
      projectDriveItems,
      integrationItems,
      studioItems,
      studioData
    );

    return state().setIn(['rfis', rfiid], updatedRFIDraft);
  },

  deleteRFIDraft: async (
    { state }: AppStateActionParams,
    rfiid: string,
    projectid: string,
    previousversionid?: string
  ): Promise<AppState> => {
    await deleteRFIDraft(rfiid, projectid, previousversionid);
    if (previousversionid) {
      return state()
        .setIn(['rfis', previousversionid, 'nextversionid'], '')
        .removeIn(['rfis', rfiid]);
    }
    return state().removeIn(['rfis', rfiid]);
  },

  publishRFIDraft: async (
    { state }: AppStateActionParams,
    rfiid: RFIId,
    rfi: NewRFI,
    files: Immutable.List<RFIFile>,
    removedAttachments: Immutable.List<RFIAttachment>,
    projectDriveItems: List<ProjectDriveSelectedItem>,
    integrationItems: List<IntegrationsSelectedItem>,
    studioItems: List<StudioProjectFileData>,
    studioData: StudioData
  ): Promise<AppState> => {
    const publishedRFIDraft = await publishRFIDraft(
      rfiid,
      rfi,
      files,
      removedAttachments,
      projectDriveItems,
      integrationItems,
      studioItems,
      studioData
    );

    return state().setIn(['rfis', rfiid], publishedRFIDraft);
  },

  postMarkupsToRFI: async function* postMarkupsToRFIAction(
    { state, actions }: AppStateActionParams,
    rfi: RFI,
    markups: Immutable.List<Markup>
  ): AsyncGenerator<AppState> {
    const updatedRFI = await postMarkupsToRFI(rfi, markups);

    yield state().setIn(['rfis', updatedRFI.id], updatedRFI);

    // fetch any markups that were created. Might need to add some retry logic here....
    // For now going to make things easy and only fetch the first markup.
    // TODO: try to create a retry() utility function that checks returned value of a call.
    if (markups.size >= 1) {
      const m = markups.get(0) as Markup;
      const mm = await getMarkup(m.markupid, m.fileid);

      yield state().setIn(['markups', mm.fileid, mm.markupid], mm);
    }

    await actions.queryRFIs();
  },

  modifyRFIForwardingList: async (
    { state }: AppStateActionParams,
    rfiid: RFIId,
    projectid: string,
    forwardList: OrderedSet<string>
  ): Promise<AppState> => {
    const updatedRFI = await modifyRFIForwardingList(
      rfiid,
      projectid,
      forwardList
    );

    return state().setIn(['rfis', rfiid], updatedRFI);
  },

  createRFIForwardActivity: async (
    { state }: AppStateActionParams,
    rfi: RFI,
    projectid: string,
    files: Immutable.List<RFIFile>,
    projectDriveItems: List<ProjectDriveSelectedItem>,
    integrationItems: List<IntegrationsSelectedItem>,
    studioItems: List<StudioProjectFileData>,
    studioData: StudioData,
    forwardActivity: NewRFIForwardActivity
  ): Promise<AppState> => {
    const updatedRFI = await createRFIForwardActivity(
      rfi,
      projectid,
      files,
      projectDriveItems,
      integrationItems,
      studioItems,
      studioData,
      forwardActivity
    );

    return state().setIn(['rfis', rfi.id], updatedRFI);
  },

  attachRfiForwardDiscussions: async (
    { state }: AppStateActionParams,
    rfi: RFI,
    projectid: string,
    discussionIds: Immutable.Set<string>
  ): Promise<AppState> => {
    const updatedRFI = await attachRfiForwardDiscussions(
      rfi,
      projectid,
      discussionIds
    );

    return state().setIn(['rfis', rfi.id], updatedRFI);
  },
  deleteRfiForwardDiscussion: async (
    { state }: AppStateActionParams,
    rfiid: string,
    projectid: string,
    id: string
  ): Promise<AppState> => {
    const updatedRFI = await deleteRfiForwardDiscussion(rfiid, projectid, id);

    return state().setIn(['rfis', rfiid], updatedRFI);
  },

  reopenRFI: async (
    { state }: AppStateActionParams,
    reopenedRfi: NewRFI,
    files: Immutable.List<RFIFile>,
    projectDriveItems: List<ProjectDriveSelectedItem>,
    integrationItems: List<IntegrationsSelectedItem>,
    studioItems: List<StudioProjectFileData>,
    studioData: StudioData
  ): Promise<AppState> => {
    const reopenedRFI = await reopenRFI(
      reopenedRfi,
      files,
      projectDriveItems,
      integrationItems,
      studioItems,
      studioData
    );

    return state()
      .setIn(['rfis', reopenedRFI.id], reopenedRFI)
      .setIn(
        ['rfis', reopenedRFI.previousversionid, 'nextversionid'],
        reopenedRFI.id
      );
  },
};

export default rfiActions;
